import React, { useState } from 'react';
import { Row, Col, Card, Alert, Table, Badge, Modal, Button } from 'react-bootstrap';
import { TrendingUp, Target, Shield, Calculator, Activity, Calendar, Zap, Clock, Info, Eye } from 'lucide-react';

const BacktestResultsDisplay = ({ backtestResults, patternAnalysis, historicalDataLength, tradeReport }) => {
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showModal, setShowModal] = useState(false);

  if (!backtestResults) return null;

  const handleShowTradeDetails = (trade) => {
    setSelectedTrade(trade);
    setShowModal(true);
  };

  return (
    <>
      {/* OHLC Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className=" text-white">
          <Modal.Title className="fw-bold d-flex align-items-center">
            <Info className="me-2" /> Trade #{selectedTrade?.tradeNumber} Technical Insights
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          {selectedTrade && (
            <Row className="g-4">
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-success bg-opacity-10 text-success fw-bold border-0">
                    ENTRY DATA (OHLC)
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Open:</span>
                      <span className="fw-bold">${selectedTrade.entryCandle?.open?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span className="opacity-75">High:</span>
                      <span className="fw-bold">${selectedTrade.entryCandle?.high?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 text-danger">
                      <span className="opacity-75">Low:</span>
                      <span className="fw-bold">${selectedTrade.entryCandle?.low?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Close:</span>
                      <span className="fw-bold">${selectedTrade.entryCandle?.close?.toFixed(2) || "N/A"}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted text-uppercase">Time:</span>
                      <span className="fw-bold">{selectedTrade.entryTime}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-danger bg-opacity-10 text-danger fw-bold border-0">
                    EXIT DATA (OHLC)
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Open:</span>
                      <span className="fw-bold">${selectedTrade.exitCandle?.open?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span className="opacity-75">High:</span>
                      <span className="fw-bold">${selectedTrade.exitCandle?.high?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 text-danger">
                      <span className="opacity-75">Low:</span>
                      <span className="fw-bold">${selectedTrade.exitCandle?.low?.toFixed(2) || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Close:</span>
                      <span className="fw-bold">${selectedTrade.exitCandle?.close?.toFixed(2) || "N/A"}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted text-uppercase">Reason:</span>
                      <Badge bg="info" className="text-uppercase small">{selectedTrade.exitReason?.replace('_', ' ')}</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={12}>
                <div className="bg-white p-3 rounded border text-center">
                  <p className="mb-1 text-muted small">Comparison Tip</p>
                  <small className="text-info">
                    Compare these <strong>Close prices</strong> with your <strong>Delta Exchange Chart</strong> at the exact timestamp to verify EMA crossovers and exit signals.
                  </small>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close Inspector</Button>
        </Modal.Footer>
      </Modal>

      {/* Results Section - Step 5 */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-lg overflow-hidden" style={{ borderRadius: '15px' }}>
            <Card.Header className="bg-success bg-gradient text-white py-4 text-center">
              <h3 className="mb-1 fw-bold">Performance Summary (Step 5)</h3>
              <p className="mb-0 opacity-75">Visual analysis of strategy execution and financial metrics</p>
            </Card.Header>
            <Card.Body className="p-5">
              <Row className="g-4 mb-5">
                <Col md={3}>
                  <div className="p-4 bg-light rounded-4 text-center shadow-sm border-bottom border-4 border-success">
                    <TrendingUp size={32} className="text-success mb-3" />
                    <span className="d-block text-muted small text-uppercase fw-bold mb-1">Total Return</span>
                    <h2 className={backtestResults.totalReturn >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}>
                      {backtestResults.totalReturn >= 0 ? "+" : ""}{backtestResults.totalReturn.toFixed(2)}%
                    </h2>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="p-4 bg-light rounded-4 text-center shadow-sm border-bottom border-4 border-primary">
                    <Target size={32} className="text-primary mb-3" />
                    <span className="d-block text-muted small text-uppercase fw-bold mb-1">Win Rate</span>
                    <h2 className="text-primary fw-bold">{backtestResults.winRate.toFixed(1)}%</h2>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="p-4 bg-light rounded-4 text-center shadow-sm border-bottom border-4 border-danger">
                    <Shield size={32} className="text-danger mb-3" />
                    <span className="d-block text-muted small text-uppercase fw-bold mb-1">Max Drawdown</span>
                    <h2 className="text-danger fw-bold">{backtestResults.maxDrawdown.toFixed(2)}%</h2>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="p-4 bg-light rounded-4 text-center shadow-sm border-bottom border-4 border-info">
                    <Calculator size={32} className="text-info mb-3" />
                    <span className="d-block text-muted small text-uppercase fw-bold mb-1">Total Trades</span>
                    <h2 className="text-info fw-bold">{backtestResults.totalTrades}</h2>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={7}>
                  <div className="p-4 border rounded-4 bg-light shadow-sm">
                    <h5 className="mb-4 fw-bold d-flex align-items-center">
                      <Activity size={18} className="me-2 text-primary" />
                      Strategy Metrics Breakdown
                    </h5>
                    <Row className="g-3">
                      <Col sm={6}>
                        <div className="p-3 bg-white rounded border d-flex justify-content-between align-items-center">
                          <small className="text-muted">Net Profit:</small>
                          <strong className={backtestResults.netProfit >= 0 ? "text-success" : "text-danger"}>${backtestResults.netProfit.toFixed(2)}</strong>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-white rounded border d-flex justify-content-between align-items-center">
                          <small className="text-muted">Profit Factor:</small>
                          <strong>{backtestResults.profitFactor ? backtestResults.profitFactor.toFixed(2) : "N/A"}</strong>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-white rounded border d-flex justify-content-between align-items-center">
                          <small className="text-muted">Avg Win:</small>
                          <strong className="text-success">+${backtestResults.avgWin?.toFixed(2) || "0.00"}</strong>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-white rounded border d-flex justify-content-between align-items-center">
                          <small className="text-muted">Avg Loss:</small>
                          <strong className="text-danger">-${Math.abs(backtestResults.avgLoss || 0).toFixed(2)}</strong>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col md={5}>
                  <div className="p-4 border rounded-4 h-100 bg-white shadow-sm">
                    <h5 className="mb-3 fw-bold">Performance Alert</h5>
                    <Alert variant={backtestResults.totalReturn >= 0 ? "success" : "danger"} className="border-0 shadow-sm p-4">
                      <div className="d-flex">
                        <div className="me-3 fs-3">
                          {backtestResults.totalReturn >= 0 ? "🚀" : "⚠️"}
                        </div>
                        <div>
                          <h6 className="fw-bold mb-1">Execution Status: SUCCESS</h6>
                          <p className="small mb-0 opacity-75">
                            Strategy processed over <strong>{historicalDataLength}</strong> real market points.
                            Current configuration yields a <strong>{backtestResults.totalReturn.toFixed(1)}%</strong> return on capital.
                          </p>
                        </div>
                      </div>
                    </Alert>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Seasonality & Pattern Analysis Report */}
      {patternAnalysis && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px', border: '1px solid #e0e0e0' }}>
              <Card.Header className="bg-dark text-white border-0 py-3 bg-gradient">
                <div className="d-flex justify-content-between align-items-center px-2">
                  <h5 className="mb-0 d-flex align-items-center fw-bold">
                    <Zap size={22} className="me-2 text-warning" />
                    Section: Seasonality & Golden Insight Results
                  </h5>
                  <Badge bg="warning" text="dark" className="px-3 py-2 fs-6 shadow-sm">Golden Insight Engine</Badge>
                </div>
              </Card.Header>
              <Card.Body className="bg-white p-4">
                {/* Top Performance Patterns */}
                <Row className="g-4 mb-5">
                  <Col md={4}>
                    <div className="p-4 bg-light rounded-4 text-center border-bottom border-4 border-success h-100 shadow-sm transition-hover border">
                      <Calendar size={32} className="text-success mb-3" />
                      <span className="text-uppercase fw-bold text-muted d-block small mb-2" style={{ letterSpacing: '1.5px' }}>Best Weekday</span>
                      <h3 className="mb-0 text-dark fw-bold">{patternAnalysis.bestDay.name}</h3>
                      <div className="mt-3 py-2 px-3 bg-success bg-opacity-10 rounded-pill d-inline-block">
                        <span className="text-success fw-bold small">+${patternAnalysis.bestDay.profit.toFixed(2)} Total P/L</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-4 bg-light rounded-4 text-center border-bottom border-4 border-primary h-100 shadow-sm transition-hover border">
                      <Clock size={32} className="text-primary mb-3" />
                      <span className="text-uppercase fw-bold text-muted d-block small mb-2" style={{ letterSpacing: '1.5px' }}>Golden Monthly Date</span>
                      <h3 className="mb-0 text-dark fw-bold">
                        {patternAnalysis.bestDate.num === "N/A" ? "N/A" : `Day ${patternAnalysis.bestDate.num}`}
                      </h3>
                      <div className="mt-3 py-2 px-3 bg-opacity-10 rounded-pill d-inline-block">
                        <span className="text-primary fw-bold small">Historical Premium</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-4 bg-light rounded-4 text-center border-bottom border-4 border-info h-100 shadow-sm transition-hover border">
                      <Target size={32} className="text-info mb-3" />
                      <span className="text-uppercase fw-bold text-muted d-block small mb-2" style={{ letterSpacing: '1.5px' }}>Consistency Edge</span>
                      <h3 className="mb-0 text-dark fw-bold">
                        {patternAnalysis.bestDay.name === "N/A" ? "0%" : (patternAnalysis.dayStats[patternAnalysis.bestDay.name]?.wins / patternAnalysis.dayStats[patternAnalysis.bestDay.name]?.count * 100 || 0).toFixed(0)}%
                      </h3>
                      <div className="mt-3 py-2 px-3 bg-info bg-opacity-10 rounded-pill d-inline-block">
                        <span className="text-info fw-bold small">Win Rate on {patternAnalysis.bestDay.name}</span>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Day-wise Performance breakdown */}
                <div className="mb-4">
                  <h6 className="fw-bold text-uppercase text-muted mb-3 d-flex align-items-center opacity-75" style={{ letterSpacing: '1px' }}>
                    <Activity size={18} className="me-2 text-primary" />
                    Weekday Performance breakdown (Historical report)
                  </h6>
                  <div className="table-responsive rounded-4 border shadow-sm overflow-hidden">
                    <Table hover borderless className="mb-0 text-center align-middle">
                      <thead className="bg-dark text-white">
                        <tr>
                          <th className="text-start ps-4 py-3 fw-normal">Weekday</th>
                          <th className="fw-normal">Total Executions</th>
                          <th className="fw-normal">Success Ratio (WR)</th>
                          <th className="fw-normal">Result (P/L)</th>
                          <th className="text-end pe-4 fw-normal">Edge Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => {
                          const stats = patternAnalysis.dayStats[day] || { count: 0, profit: 0, wins: 0 };
                          const wr = stats.count > 0 ? (stats.wins / stats.count * 100).toFixed(0) : 0;
                          return (
                            <tr key={day} className="border-bottom">
                              <td className="text-start ps-4 fw-bold py-3">{day}</td>
                              <td className="text-muted">{stats.count} Trades</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center">
                                  <div className="progress flex-grow-0 me-3" style={{ height: '12px', width: '100px', borderRadius: '6px', background: '#f0f0f0' }}>
                                    <div className={`progress-bar bg-${wr >= 50 ? 'success' : 'danger'} bg-gradient shadow-sm`} style={{ width: `${wr}%` }}></div>
                                  </div>
                                  <small className="fw-bold fs-6">{wr}%</small>
                                </div>
                              </td>
                              <td className={stats.profit >= 0 ? "text-success fw-bold fs-5" : "text-danger fw-bold fs-5"}>
                                {stats.profit >= 0 ? '+' : ''}${stats.profit.toFixed(2)}
                              </td>
                              <td className="text-end pe-4">
                                <Badge pill bg={stats.profit > 0 ? "success" : "secondary"} className="opacity-75 px-4 py-2">
                                  {stats.profit > 0 ? "High Advantage" : "Low Advantage"}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* Final Insight Banner */}
                <Alert variant="info" className="border-0 shadow-sm p-4 mb-0 rounded-4  bg-opacity-10 border-start border-5 border-primary">
                  <div className="d-flex align-items-center">
                    <div className=" rounded-pill p-3 me-4 shadow d-flex align-items-center justify-content-center" style={{ width: '65px', height: '65px' }}>
                      <Zap size={32} className="text-white" />
                    </div>
                    <div>
                      <h4 className="mb-1 fw-bold text-primary">Strategic Advantage Summary</h4>
                      <p className="mb-0 text-dark opacity-75 fs-6">
                        Our system suggests prioritizing entries on <strong>{patternAnalysis.bestDay.name}s</strong> for maximum consistency.
                        Trading on this day historically delivers a <strong>{(patternAnalysis.dayStats[patternAnalysis.bestDay.name]?.wins / patternAnalysis.dayStats[patternAnalysis.bestDay.name]?.count * 100 || 0).toFixed(0)}% win rate</strong>.
                        {patternAnalysis.bestDate.num !== "N/A" && (
                          <>
                            Additionally, the <strong>{patternAnalysis.bestDate.num}th</strong> calendar day has shown the highest average return per trade.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Detailed Trade Report Card */}
      {tradeReport && tradeReport.length > 0 && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
              <Card.Header className="bg-info bg-gradient text-white d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <Activity size={18} className="me-2" />
                  Detailed Trade-by-Trade Execution Log
                </h5>
                <Badge bg="light" text="dark" className="px-3">{tradeReport.length} Executed Signals</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <Table hover className="mb-0 text-center align-middle">
                    <thead className="bg-light sticky-top">
                      <tr className="small text-muted text-uppercase">
                        <th>No.</th>
                        <th>Side</th>
                        <th className="text-start">Entry Point</th>
                        <th>Entry Price</th>
                        <th>Entry Ind. (F/S)</th>
                        <th className="text-start">Exit Point</th>
                        <th>Exit Price</th>
                        <th>Exit Ind. (F/S)</th>
                        <th>PnL</th>
                        <th>Growth %</th>
                        <th>Duration</th>
                        <th>Exit Status</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeReport.map((trade, index) => (
                        <tr key={index}>
                          <td className="fw-bold">{trade.tradeNumber}</td>
                          <td>
                            <Badge bg={trade.type === "LONG" ? "success" : "danger"} className="px-3">
                              {trade.type}
                            </Badge>
                          </td>
                          <td className="text-start small">{trade.entryTime}</td>
                          <td className="fw-bold">${trade.entryPrice}</td>
                          <td className="small">
                            <span className="text-primary fw-bold">{trade.fastEMA}</span>
                            <span className="mx-1 text-muted">/</span>
                            <span className="text-secondary fw-bold">{trade.slowEMA}</span>
                          </td>
                          <td className="text-start small">{trade.exitTime || "Active"}</td>
                          <td className="fw-bold">${trade.exitPrice || "-"}</td>
                          <td className="small">
                            {trade.exitTime !== "Active" ? (
                              <>
                                <span className="text-primary fw-bold text-opacity-75">{trade.exitFastEMA}</span>
                                <span className="mx-1 text-muted">/</span>
                                <span className="text-secondary fw-bold text-opacity-75">{trade.exitSlowEMA}</span>
                              </>
                            ) : (
                              <span className="text-muted">Live</span>
                            )}
                          </td>
                          <td className={trade.profit >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}>
                            ${trade.profit.toFixed(2)}
                          </td>
                          <td>
                            <Badge bg={trade.profitPercent >= 0 ? "success" : "danger"} className="bg-opacity-10 text-dark">
                              {trade.profitPercent >= 0 ? "+" : ""}{trade.profitPercent.toFixed(2)}%
                            </Badge>
                          </td>
                          <td className="small text-muted">{trade.duration}</td>
                          <td>
                            <small className="fw-bold text-muted">{trade.exitReason?.replace('_', ' ') || "SIGNAL"}</small>
                          </td>
                          <td>
                            <Button variant="outline-info" size="sm" onClick={() => handleShowTradeDetails(trade)} className="p-1 rounded-circle">
                              <Eye size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default BacktestResultsDisplay;
